import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Divider,
    FormControlLabel,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {useSemanticSearch} from 'Hooks/useSemanticSearch';
import {SemanticSearchBar} from 'Components/SemanticSearchBar';
import {SearchScoreChip} from 'Components/SearchScoreChip';
import {UnifiedSearchHit, UnifiedSearchHitType} from 'apiClient/Api';

type TypeFilter = 'all' | 'device' | 'xml_example';

/**
 * Internal/Office-only landing page for the unified semantic search across
 * Devices (Anlagen) and XmlExamples (Service-Beispiele). Hits are mixed and
 * sorted by score; the type discriminator is shown as a chip and the click
 * target depends on the type.
 */
const UnifiedSearch: React.FC = () => {
    const {t} = useTranslation();
    useDocumentTitle('unifiedSearch.title');
    const context = useApplicationContext();
    const navigate = useNavigate();

    const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

    // The search hook stores results as Map<id, score>; for unified search we
    // need the full hit objects to render correctly. We keep a parallel cache
    // of hits keyed by id, populated inside searchFn.
    const [hitCache, setHitCache] = useState<UnifiedSearchHit[]>([]);

    const search = useSemanticSearch<UnifiedSearchHit>({
        searchFn: async (query) => {
            const types =
                typeFilter === 'device' ? ['device']
                    : typeFilter === 'xml_example' ? ['xml_example']
                        : undefined;
            const response = await context.api.search.searchSearchUnified({query, limit: 50, types});
            const data = response.data ?? [];
            setHitCache(data);
            return data;
        },
        extractKey: (hit) => ({key: hit.id, score: hit.normalizedScore ?? hit.score}),
    });

    const orderedHits = useMemo(() => {
        if (!search.hits) return [];
        return [...hitCache].sort((a, b) => (b.normalizedScore ?? b.score ?? 0) - (a.normalizedScore ?? a.score ?? 0));
    }, [hitCache, search.hits]);

    const navigateForHit = (hit: UnifiedSearchHit) => {
        if (!hit.id) return;
        if (hit.type === UnifiedSearchHitType.XmlExample) {
            navigate(`/admin/xml-examples/${hit.id}`);
        } else if (hit.type === UnifiedSearchHitType.Device) {
            navigate(`/device/${hit.id}`);
        }
    };

    const typeLabel = (hit: UnifiedSearchHit): string =>
        hit.type === UnifiedSearchHitType.XmlExample
            ? t('unifiedSearch.types.xmlExample')
            : t('unifiedSearch.types.device');

    const typeColor = (hit: UnifiedSearchHit): 'primary' | 'secondary' | 'default' =>
        hit.type === UnifiedSearchHitType.XmlExample ? 'secondary' : 'primary';

    return (
        <Box sx={{width: '100%', p: 2}}>
            <Box sx={{mb: 3}}>
                <Typography variant="h4" component="h1">
                    {t('unifiedSearch.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('unifiedSearch.subtitle')}
                </Typography>
            </Box>

            <Box sx={{maxWidth: 720, mb: 2}}>
                <SemanticSearchBar
                    value={search.query}
                    onChange={search.setQuery}
                    loading={search.loading}
                    unavailable={search.unavailable}
                    error={search.error}
                    matchCount={search.hits ? orderedHits.length : undefined}
                    placeholder={t('unifiedSearch.placeholder')}
                />
            </Box>

            <Stack direction="row" spacing={2} sx={{flexWrap: 'wrap', rowGap: 1, mb: 3, alignItems: 'center'}}>
                <Typography variant="body2" color="text.secondary">{t('unifiedSearch.filterByType')}:</Typography>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={typeFilter !== 'xml_example'}
                            onChange={(_, checked) => {
                                if (checked && typeFilter === 'xml_example') setTypeFilter('all');
                                else if (!checked) setTypeFilter('xml_example');
                            }}
                        />
                    }
                    label={t('unifiedSearch.types.device')}
                />
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={typeFilter !== 'device'}
                            onChange={(_, checked) => {
                                if (checked && typeFilter === 'device') setTypeFilter('all');
                                else if (!checked) setTypeFilter('device');
                            }}
                        />
                    }
                    label={t('unifiedSearch.types.xmlExample')}
                />
            </Stack>

            {search.hits && orderedHits.length === 0 && !search.loading && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                            {t('unifiedSearch.noResults')}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            <Stack spacing={1.5}>
                {orderedHits.map(hit => (
                    <Card key={`${hit.type}-${hit.id}`} variant="outlined">
                        <CardActionArea onClick={() => navigateForHit(hit)}>
                            <CardContent>
                                <Stack
                                    direction={{xs: 'column', sm: 'row'}}
                                    spacing={2}
                                    sx={{alignItems: {sm: 'flex-start'}}}
                                >
                                    <Box sx={{flex: 1, minWidth: 0}}>
                                        <Stack direction="row" spacing={1} sx={{alignItems: 'center', mb: 0.5, flexWrap: 'wrap', rowGap: 0.5}}>
                                            <Chip
                                                size="small"
                                                color={typeColor(hit)}
                                                label={typeLabel(hit)}
                                                sx={{fontWeight: 600}}
                                            />
                                            {hit.targetVersion && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`v${hit.targetVersion}`}
                                                />
                                            )}
                                            {hit.customerDeviceId && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={hit.customerDeviceId}
                                                    sx={{fontFamily: 'monospace'}}
                                                />
                                            )}
                                        </Stack>
                                        <Typography variant="h6" sx={{lineHeight: 1.2}}>
                                            {hit.title ?? '—'}
                                        </Typography>
                                        {hit.description && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.5,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {hit.description}
                                            </Typography>
                                        )}
                                        {(hit.features?.length ?? 0) > 0 && (
                                            <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1}}>
                                                {hit.features!.map(f => (
                                                    <Chip key={f} size="small" variant="outlined" label={f}/>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                    <Stack direction="column" spacing={0.5} sx={{alignItems: 'flex-end', flexShrink: 0}}>
                                        {(hit.normalizedScore ?? hit.score) != null && (
                                            <SearchScoreChip score={hit.normalizedScore ?? hit.score!}/>
                                        )}
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
            </Stack>

            {!search.hits && (
                <Box sx={{mt: 4}}>
                    <Divider sx={{mb: 2}}/>
                    <Typography variant="caption" color="text.secondary">
                        {t('unifiedSearch.hint')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default UnifiedSearch;
